import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ExternalLink, ReceiptText } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { formatCount, formatCurrencyGbp } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Card } from "../../primitives/card";

type CardState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; count: number; value: number };

/**
 * The caller's own jobs that are done but not yet invoiced — "what of mine is
 * ready to invoice?". Reads the jobs list's own aggregate (awaiting_invoice
 * bucket, `mine`, all-time) rather than a second definition of the bucket,
 * and links to that exact list, so the number and the page always agree.
 */
export function AwaitingInvoiceMetricCard() {
  const adapter = useDataAdapter();
  const [state, setState] = useState<CardState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    adapter
      .getChargeableJobs({
        bucket: "awaiting_invoice",
        mine: true,
        pageSize: 1,
      })
      .then((resp) => {
        if (cancelled) return;
        setState({
          status: "loaded",
          count: resp.total,
          value: resp.totalBookedValue ?? 0,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  return (
    <Link
      to={paths.jobs({ bucket: "awaiting_invoice", mine: true, range: "all" })}
    >
      <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
              <ReceiptText className="size-4" />
              Awaiting invoice
            </div>
            <div className="text-3xl font-bold text-foreground">
              {state.status === "loaded" ? formatCount(state.count) : "—"}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {state.status === "loading" && "Loading…"}
              {state.status === "error" && "Couldn't load your jobs"}
              {state.status === "loaded" &&
                (state.count === 0
                  ? "Nothing of yours waiting to be invoiced"
                  : `${formatCurrencyGbp(state.value)} delivered, not yet invoiced`)}
            </div>
          </div>
          <div className="p-2 rounded-full bg-muted">
            <ReceiptText className="size-5 text-muted-foreground" />
          </div>
        </div>

        <div className="text-xs text-primary inline-flex items-center gap-1">
          View my jobs awaiting invoice
          <ExternalLink className="size-3" />
        </div>
      </Card>
    </Link>
  );
}
